<template>
  <inline-loader v-if="isLoading" />

  <div class="profile-badge-list">
    <ul>
      <li
        v-for="(badge, index) in pageNoToBadgesListMap.get(currentPageNo)"
        :key="index"
        :class="['badge-item', { selected: selectedBadgeIndex === getAbsoluteBadgeIndex(index) }]"
      >
        <DigitalBadge85
          v-if="!hasCustomBadgeImage(badge)"
          class="badge-image"
          @click="handleClick(index)"
        />
        <img
          v-else
          :src="badge.imageUrl"
          class="badge-image"
          alt="custom-badge-image"
          @click="handleClick(index)"
        />
      </li>
    </ul>
    <pagination
      :current-page="currentPageNo"
      :total-page="totalPages"
      :simple="true"
      @change="handlePageChange"
    />
  </div>
</template>

<script setup lang="ts">
import Pagination from "@plugins/common/components/Pagination.vue";
import DigitalBadge85 from "@images/game/DigitalBadge85.svg?component";
import { ref, computed, onMounted } from "vue";
import { Badge } from "@modules/profile/profile-types.ts";

interface Props {
  listOfBadges: Badge[];
}

const props = defineProps<Props>();
const emit = defineEmits(["selectedBadgeIndex"]);

// Data
const currentPageNo = ref(1);
const badgesPerPage = ref(12);
const selectedBadgeIndex = ref(0);
const pageNoToBadgesListMap = ref<Map<number, Badge[]>>(new Map());
const isLoading = ref(true);

// Computed
const totalPages = computed(() => {
  return props.listOfBadges ? Math.ceil(props.listOfBadges.length / badgesPerPage.value) : 0;
});

// Methods
const handleClick = (badgeIndex) => {
  // badgesPerPage = 12
  // p1: [0, 1, 2, ...] --> 0 + index
  // p2: [0, 1, 2, ...] --> 12 + index
  // To account for page number and scattered indexes, multiply badgesPerPage to get to actual index in list

  selectedBadgeIndex.value = getAbsoluteBadgeIndex(badgeIndex);
  emit("selectedBadgeIndex", selectedBadgeIndex.value);
};

const hasCustomBadgeImage = (badge) => {
  return !!badge.image;
};

const sortBadgeListIntoPages = (listOfBadges) => {
  const requiredNoOfPages = totalPages.value;

  // if no pages map will be empty
  for (let page = 0; page < requiredNoOfPages; page++) {
    const startIndex = page * badgesPerPage.value;
    const endIndex = startIndex + badgesPerPage.value;

    pageNoToBadgesListMap.value.set(page + 1, listOfBadges.slice(startIndex, endIndex));
  }
};

const handlePageChange = (pageNumber) => {
  currentPageNo.value = pageNumber;
};

const getAbsoluteBadgeIndex = (badgeIndex) => {
  // this is the index of the badge in the actual list passed in (listOfBadges)
  return (currentPageNo.value - 1) * badgesPerPage.value + badgeIndex;
};

onMounted(() => {
  sortBadgeListIntoPages(props.listOfBadges);
  isLoading.value = false;
});
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.profile-badge-list {
  > ul {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: $spacing-06;
    margin-bottom: $spacing-07;

    @include media("desktop") {
      grid-template-columns: repeat(6, 1fr);
      grid-gap: $spacing-05 $spacing-06;
    }

    > .badge-item {
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      width: 82px;
      height: 82px;
      margin: auto;
      border: 2px solid transparent;
      border-radius: 10px;
      overflow: hidden;
      cursor: pointer;

      &.selected {
        border-color: $inverse-link;
      }

      > img {
        position: absolute;
        width: 100%;
        height: auto;
      }

      > svg {
        position: absolute;
      }
    }
  }
}
</style>
